import React, { useState, useEffect, useMemo } from 'react';
import './Aoe2UnitDirectory.scss';

const FILTER_CONFIG_AOE2 = {
  age: { label: 'Age', options: ['Dark Age', 'Feudal Age', 'Castle Age', 'Imperial Age'] },
  unit_type: { label: 'Type', options: ['Infantry', 'Archer', 'Cavalry', 'Siege', 'Ship', 'Monk', 'Civilian'] },
  building: { label: 'Building', options: ['Barracks', 'Archery Range', 'Stable', 'Siege Workshop', 'Dock', 'Monastery', 'Castle', 'Market', 'Town Center', 'Fortified Church'] }
};

const SESSION_STORAGE_FILTERS_KEY_AOE2 = 'aoe2UnitsDirectoryFiltersV1';

const getDefaultFiltersAoe2 = () => {
  const defaults = {};
  for (const key in FILTER_CONFIG_AOE2) {
    defaults[key] = [...FILTER_CONFIG_AOE2[key].options];
  }
  return defaults;
};

const Aoe2UnitDirectory = ({ allUnits, guessedUnits, show, onClose, onUnitSelect }) => {
  const [filters, setFilters] = useState(() => {
    try {
      const storedFilters = sessionStorage.getItem(SESSION_STORAGE_FILTERS_KEY_AOE2);
      if (storedFilters) {
        const parsedFilters = JSON.parse(storedFilters);
        // Basic validation to ensure stored filters match current config
        let isValid = true;
        for (const key in FILTER_CONFIG_AOE2) {
          if (!parsedFilters[key] || !Array.isArray(parsedFilters[key])) {
            isValid = false;
            break;
          }
        }
        if (isValid) return parsedFilters;
      }
    } catch (error) {
      console.error("Error reading AoE2 unit filters from session storage:", error);
    }
    return getDefaultFiltersAoe2();
  });

  useEffect(() => {
    try {
      sessionStorage.setItem(SESSION_STORAGE_FILTERS_KEY_AOE2, JSON.stringify(filters));
    } catch (error) {
      console.error("Error saving AoE2 unit filters to session storage:", error);
    }
  }, [filters]);

  const handleFilterChange = (categoryKey, option, isRightClick = false) => {
    setFilters(prevFilters => {
      if (isRightClick) {
        // Right click: Turn off all options in this category except the clicked one
        // and ensure the clicked one is ON
        return {
          ...prevFilters,
          [categoryKey]: [option],
        };
      } else {
        // Left click: Toggle the clicked option on/off
        const currentSelections = prevFilters[categoryKey] || [];
        const newSelections = currentSelections.includes(option)
          ? currentSelections.filter(item => item !== option)
          : [...currentSelections, option];
        
        return { ...prevFilters, [categoryKey]: newSelections };
      }
    });
  };

  const handleResetAllFilters = () => setFilters(getDefaultFiltersAoe2());

  const filteredAndSortedUnits = useMemo(() => {
    if (!allUnits || allUnits.length === 0) return [];

    return allUnits
      .filter(unit => {
        for (const categoryKey in filters) {
          if (!filters[categoryKey].includes(unit[categoryKey])) {
            return false;
          }
        }
        return true;
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [allUnits, filters]);

  if (!show) {
    return null;
  }

  const handleUnitClick = (unitName) => {
    if (onUnitSelect) {
      onUnitSelect(unitName);
    }
    onClose();
  };

  const areFiltersDefault = () => {
    const defaultFilters = getDefaultFiltersAoe2();
    return JSON.stringify(filters) === JSON.stringify(defaultFilters);
  };
  const isAnyFilterActive = !areFiltersDefault();

  return (
    <div className="unit-directory__overlay" onClick={onClose}>
      <div className="unit-directory__panel" onClick={(e) => e.stopPropagation()}>
        <button className="unit-directory__close-button" onClick={onClose}>✕</button>
        <div className="unit-directory__header">
            <h2>Unit Directory</h2>
            {isAnyFilterActive && (
                <button onClick={handleResetAllFilters} className="unit-directory__reset-button">
                    Reset Filters
                </button>
            )}
        </div>

        <div className="unit-directory__filters">
          {Object.entries(FILTER_CONFIG_AOE2).map(([categoryKey, config]) => (
            <div key={categoryKey} className="unit-directory__filter-category">
              <span className="unit-directory__filter-label">{config.label}:</span>
              <div className="unit-directory__filter-options">
                {config.options.map(option => (
                  <button
                    key={option}
                    className={`unit-directory__filter-button ${
                      filters[categoryKey]?.includes(option) ? 'unit-directory__filter-button--selected' : ''
                    }`}
                    onClick={() => handleFilterChange(categoryKey, option, false)}
                    onContextMenu={(e) => {
                      e.preventDefault(); // Prevent default context menu
                      handleFilterChange(categoryKey, option, true);
                    }}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        <ul className="unit-directory__list">
          {filteredAndSortedUnits.length > 0 ? (
            filteredAndSortedUnits.map((unit) => (
              <li
                key={unit.name} 
                className={`unit-directory__list-item ${
                  guessedUnits.includes(unit.name) ? 'unit-directory__list-item--guessed' : ''
                }`}
                onClick={() => {
                  if (!guessedUnits.includes(unit.name)) {
                    handleUnitClick(unit.name);
                  }
                }}
                style={!guessedUnits.includes(unit.name) ? { cursor: 'pointer' } : {}}
              >
                <div className="unit-directory__item-content">
                  <span className="unit-directory__item-name">{unit.name}</span>
                  <div className="unit-directory__item-details">
                    <span className="unit-directory__item-detail">{unit.age}</span>
                    <span className="unit-directory__item-detail">{unit.unit_type}</span>
                    <span className="unit-directory__item-detail">{unit.building}</span>
                  </div>
                </div>
              </li>
            ))
          ) : (
            <li className="unit-directory__list-item unit-directory__list-item--empty">
              No units match the current filters.
            </li>
          )}
        </ul>
      </div>
    </div>
  );
};

export default Aoe2UnitDirectory;